import React from "react";
import { motion } from "framer-motion";
import {
  Database,
  ShieldCheck,
  Scale,
  Network,
  Wifi,
  Boxes,
  Users,
  Store,
  Fingerprint,
  Calculator,
  Sparkles,
  Bot,
  MessageSquare,
  Infinity as InfinityIcon,
} from "lucide-react";
import { Reveal } from "./motion";
import { AnimatedCounter } from "./AnimatedCounter";

const orbitModules = [
  { icon: Boxes, label: "ERP", angle: 0 },
  { icon: Users, label: "CRM", angle: 72 },
  { icon: Store, label: "PDV", angle: 144 },
  { icon: Fingerprint, label: "Ponto", angle: 216 },
  { icon: Calculator, label: "Contabilidade", angle: 288 },
];

const OrbitDiagram = () => {
  const size = 260;
  const radius = 100;
  const center = size / 2;

  return (
    <div
      className="relative mx-auto flex items-center justify-center overflow-hidden rounded-3xl border border-navy-100 bg-gradient-to-br from-white to-ice p-8 shadow-xl dark:border-white/10 dark:from-navy-900 dark:to-navy-950"
      data-testid="unified-orbit"
    >
      <div className="absolute left-6 top-6 z-30 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-lavender/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-navy-800 dark:border-white/15 dark:bg-white/10 dark:text-lavender">
        <Sparkles className="h-3 w-3" /> Banco de dados unificado
      </div>

      <div className="absolute right-6 top-6 z-30 text-right">
        <p className="text-[10px] font-bold uppercase tracking-widest text-navy-400 dark:text-white/50">
          1 conta
        </p>
        <p className="text-2xl font-black text-navy-800 dark:text-white">
          5 módulos
        </p>
      </div>

      <div className="relative" style={{ width: size, height: size }}>
        {/* Central Talos Core */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex h-20 w-20 flex-col items-center justify-center rounded-full bg-navy-800 text-white shadow-2xl border-2 border-lavender/40">
          <Bot className="h-6 w-6 text-lavender" />
          <span className="text-[10px] font-black tracking-wider uppercase mt-0.5">Talos</span>
        </div>

        {/* 5 Orbiting modules */}
        {orbitModules.map((m) => {
          const rad = (m.angle * Math.PI) / 180;
          const x = center + radius * Math.cos(rad) - 22;
          const y = center + radius * Math.sin(rad) - 22;
          const Icon = m.icon;
          return (
            <div
              key={m.label}
              style={{ left: x, top: y }}
              className="absolute z-20 flex h-11 w-11 flex-col items-center justify-center rounded-full bg-white dark:bg-navy-800 text-navy-800 dark:text-white shadow-md border border-navy-100 dark:border-white/10"
            >
              <Icon className="h-4 w-4" />
              <span className="text-[7px] font-extrabold uppercase mt-0.5">{m.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Differentiators = () => {
  return (
    <section
      id="diferenciais"
      className="relative bg-white py-24 transition-colors duration-500 dark:bg-navy-900 md:py-32"
      data-testid="differentiators-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-lavender/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-navy-800 dark:border-white/10 dark:bg-navy-800 dark:text-lavender">
            <Sparkles className="h-3.5 w-3.5" /> Por que o MUFT é diferente
          </div>
          <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
            Construído para simplificar a vida de quem produz e vende
          </h2>
          <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
            Comparamos o que o mercado tradicional entrega com o novo padrão que o MUFT traz.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <OrbitDiagram />
          </div>

          <div className="lg:col-span-6 space-y-4">
            <div className="rounded-2xl border border-navy-100 bg-ice dark:border-white/10 dark:bg-navy-800/80 p-5 space-y-2">
              <div className="flex items-center gap-2 text-navy-800 dark:text-lavender font-black text-base md:text-lg">
                <InfinityIcon className="h-5 w-5" /> Usuários e equipe 100% ilimitados
              </div>
              <p className="text-xs md:text-sm font-medium text-navy-500 dark:text-navy-100/70 leading-relaxed">
                Você nunca pagará taxa extra por usuário cadastrado, vendedor no balcão ou funcionário no ponto. Cadastre toda a sua equipe sem preocupação.
              </p>
            </div>

            <div className="rounded-2xl border border-navy-100 bg-ice dark:border-white/10 dark:bg-navy-800/80 p-5 space-y-2">
              <div className="flex items-center gap-2 text-navy-800 dark:text-lavender font-black text-base md:text-lg">
                <MessageSquare className="h-5 w-5 text-emerald-500" /> Provedor de Tecnologia Meta · WhatsApp em 1 clique
              </div>
              <p className="text-xs md:text-sm font-medium text-navy-500 dark:text-navy-100/70 leading-relaxed">
                Somos Provedor de Tecnologia Meta oficial. Integre o WhatsApp Business da sua empresa em 1 clique via Cloud API — envie cobranças Pix, ofertas e pós-venda sem instabilidade ou risco de banimento de chip.
              </p>
            </div>

            <div className="rounded-2xl border border-navy-100 bg-ice dark:border-white/10 dark:bg-navy-800/80 p-5 space-y-2">
              <div className="flex items-center gap-2 text-navy-800 dark:text-lavender font-black text-base md:text-lg">
                <Bot className="h-5 w-5" /> Talos — Inteligência que executa
              </div>
              <p className="text-xs md:text-sm font-medium text-navy-500 dark:text-navy-100/70 leading-relaxed">
                A IA do MUFT não é só texto. Ela tem ferramentas nativas para abrir caixa, emitir alertas fiscais, buscar estoque e gerar ordens de compra em tempo real.
              </p>
            </div>

            <div className="rounded-2xl border border-navy-100 bg-ice dark:border-white/10 dark:bg-navy-800/80 p-5 space-y-2">
              <div className="flex items-center gap-2 text-navy-800 dark:text-lavender font-black text-base md:text-lg">
                <ShieldCheck className="h-5 w-5" /> Multi-filial e segurança corporativa
              </div>
              <p className="text-xs md:text-sm font-medium text-navy-500 dark:text-navy-100/70 leading-relaxed">
                Escale de 1 para até 30 filiais mantendo estoque centralizado, caixas independentes e regras fiscais específicas por estado.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Differentiators;
